/**
 * The stored poster of a moment, rendered (FEAT-038 wave 3).
 *
 * The whole point is what it does NOT do: no video element, no seek, no decode,
 * no object URL over the media itself. It renders ~20 KB that were taken at
 * capture and have been sitting in the `media-meta` row ever since. A moment
 * with no poster (an upgrade's older drafts, a canvas that could not encode)
 * falls back to the glyph that says what it is — the wave-1 behaviour, kept as
 * the floor rather than as the rule.
 */
import { useEffect, useState } from 'react';
import { Camera, Image, Play } from 'lucide-react';

interface MediaPosterProps {
  poster?: { bytes: ArrayBuffer; type: string };
  mediaType: 'photo' | 'video';
  className?: string;
  /** The play badge over a clip. Off where the tile says "video" some other way. */
  badge?: boolean;
}

export function MediaPoster({
  poster,
  mediaType,
  className = '',
  badge = true,
}: MediaPosterProps) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!poster) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(new Blob([poster.bytes], { type: poster.type }));
    setUrl(next);
    // Revoked when the tile goes away — a sheet opened and closed twenty times
    // must not leave twenty blobs behind.
    return () => {
      URL.revokeObjectURL(next);
      setUrl(null);
    };
  }, [poster]);

  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden bg-surface-sunken ${className}`}
    >
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : mediaType === 'video' ? (
        <Camera size={22} className="text-text-muted" />
      ) : (
        <Image size={22} className="text-text-muted" />
      )}
      {url && badge && mediaType === 'video' && (
        <span className="absolute inset-0 flex items-center justify-center text-white">
          <Play size={14} fill="currentColor" className="drop-shadow" />
        </span>
      )}
    </div>
  );
}

export default MediaPoster;
